<script setup lang="ts">
import {
  ErrorSnackbar,
  LanguageSelector,
  useUserStore,
} from '@blueprint/frontend-basics'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView, useRoute } from 'vue-router'

import type { ShopDto } from '@/models/dtos/shop.dto.ts'
import type { useServiceStore } from './stores/service.store'

const { t } = useI18n()
const userStore = useUserStore()
const serviceStore = inject<ReturnType<typeof useServiceStore>>('serviceStore')
const route = useRoute()
const shop = ref<ShopDto | null>(null)

const shopId = computed(() => route.params.shopId as string | undefined)

const toolbarTitle = computed(() => shop.value?.name ?? t('general.appName'))

const loadShop = async (id: string): Promise<void> => {
  if (!id || !serviceStore?.shopService) {
    shop.value = null
    return
  }
  try {
    shop.value = await serviceStore.shopService.getShop(id)
  } catch {
    shop.value = null
  }
}

watch(
  shopId,
  (newShopId) => {
    if (newShopId) {
      void loadShop(newShopId)
    } else {
      shop.value = null
    }
  },
  { immediate: true },
)

onMounted(() => {})
</script>

<template>
  <v-app>
    <v-app-bar app>
      <v-toolbar-title>{{ toolbarTitle }}</v-toolbar-title>
      <v-spacer />
      <v-progress-circular
        v-if="userStore.isLoading"
        color="primary"
        indeterminate
      />
      <v-btn
        v-else-if="userStore.isAnonymous && serviceStore"
        @click="serviceStore.authService.startLoginProcess()"
      >
        {{ t('general.login') }}
      </v-btn>
      <v-btn v-else-if="serviceStore" @click="serviceStore.authService.logout()">
        {{ t('general.logout') }}
      </v-btn>
      <LanguageSelector />
    </v-app-bar>

    <v-main>
      <RouterView />
    </v-main>

    <ErrorSnackbar />
  </v-app>
</template>
